import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getPageItems, receivedRoles } from './actions'
import { makeSelectorById, makeSelectorRole } from './selectors'
import { makeSelectMetadataByRequestId, selectStatusByRequestId } from '../busy/selectors'
import { busy, notBusy } from '../busy/reducer'
import api from '../api'
import { handleAddErrors, handleDeleteErrors, handleGetErrors, handleUpdateErrors } from '../error/actions'
import { useRequestId } from '../../hooks/useRequestId'

export function useGetRoleByIdQuery (id) {
  const reqId = useRequestId([id])
  const dispatch = useDispatch()
  const [refetching, setRefetching] = useState(false)
  const selectRoleById = useMemo(makeSelectorById, [])

  const status = useSelector((state) => selectStatusByRequestId(state, reqId))
  const data = useSelector((state) => selectRoleById(state, id))

  const effects = useCallback((requestId, params) => {
    dispatch(busy({ requestId }))
    api.getRoles(params).then(result => {
      dispatch(receivedRoles(result.data.value))
      dispatch(notBusy({ requestId }))
    }).catch(handleGetErrors('role', dispatch, requestId))
  }, [dispatch])

  useEffect(() => {
    if ((status === undefined || refetching) && id) {
      effects(reqId, { query: [{ key: 'id', op: '=', value: id }] })
      setRefetching(false)
    }
  }, [effects, id, reqId, status, refetching, setRefetching])

  const refetch = useCallback(() => {
    setRefetching(true)
  }, [setRefetching])

  const isLoading = status === 'pending' || status === undefined
  const isError = status === 'rejected'
  const isSuccessful = status === 'resolved'
  const isUnitialized = status === undefined
  return { isUnitialized, isError, isSuccessful, data, refetch, isLoading }
}

export function useGetRoleQuery (params = { limit: 50 }) {
  const reqId = useRequestId()
  const dispatch = useDispatch()
  const selectRoles = useMemo(makeSelectorRole, [])

  const status = useSelector((state) => selectStatusByRequestId(state, reqId))
  const data = useSelector(selectRoles)

  const effects = useCallback((requestId, params) => {
    dispatch(busy({ requestId }))
    api.getRoles(params).then(result => {
      dispatch(receivedRoles(result.data.value))
      dispatch(notBusy({ requestId }))
    }).catch(handleGetErrors('role', dispatch, requestId))
  }, [dispatch])

  useEffect(() => {
    if (status === undefined) {
      effects(reqId, params)
    }
  }, [effects, params, reqId, status])

  const refetch = useCallback((params) => {
    effects(reqId, params)
  }, [reqId])

  const isLoading = status === 'pending' || status === undefined
  const isError = status === 'rejected'
  const isSuccessful = status === 'resolved'
  const isUnitialized = status === undefined
  return { isUnitialized, isError, isSuccessful, data, refetch, isLoading }
}

export function useAddRoleMutation () {
  const requestId = useRequestId()
  const dispatch = useDispatch()
  const selectMetadataByRequestId = useMemo(makeSelectMetadataByRequestId, [])

  const metadata = useSelector((state) => selectMetadataByRequestId(state, requestId))
  const status = useSelector((state) => selectStatusByRequestId(state, requestId))

  const mutation = useCallback((role) => {
    dispatch(busy({ requestId }))
    api.addRole(role).then(result => {
      dispatch(notBusy({ requestId, id: result.data.value?.id }))
    }).catch(handleAddErrors('role', dispatch))
  }, [dispatch, requestId])

  const isUnitialized = status === 'pending' || status === undefined
  const isLoading = status === 'pending'
  const isSuccessful = status === 'resolved'
  const isError = status === 'rejected'

  return { metadata, mutation, isLoading, isError, isUnitialized, isSuccessful }
}

export function useUpdateRoleMutation () {
  const requestId = useRequestId()
  const dispatch = useDispatch()

  const selectMetadataByRequestId = useMemo(makeSelectMetadataByRequestId, [])
  const metadata = useSelector((state) => selectMetadataByRequestId(state, requestId))
  const status = useSelector((state) => selectStatusByRequestId(state, requestId))

  const mutation = useCallback((role) => {
    dispatch(busy({ requestId }))
    api.updateRole(role).then(result => {
      dispatch(notBusy({ requestId, id: result.data.value?.id }))
    }).catch(handleUpdateErrors('role', dispatch, requestId))
  }, [dispatch, requestId])

  const isUnitialized = status === undefined
  const isLoading = status === 'pending'
  const isSuccessful = status === 'resolved'
  const isError = status === 'rejected'

  return { metadata, mutation, isLoading, isError, isUnitialized, isSuccessful }
}

export function useDeleteRoleMutation () {
  const requestId = useRequestId()
  const dispatch = useDispatch()

  const selectMetadataByRequestId = useMemo(makeSelectMetadataByRequestId, [])
  const metadata = useSelector((state) => selectMetadataByRequestId(state, requestId))
  const status = useSelector((state) => selectStatusByRequestId(state, requestId))

  const mutation = useCallback((role) => {
    dispatch(busy({ id: role.id, requestId }))
    api.deleteRole(role).then(() => {
      dispatch(notBusy({ requestId }))
      dispatch(getPageItems())
    }).catch(handleDeleteErrors('role', dispatch, requestId))
  }, [dispatch, requestId])

  const isUnitialized = status === undefined
  const isLoading = status === 'pending'
  const isSuccessful = status === 'resolved'
  const isError = status === 'rejected'

  return { metadata, mutation, isLoading, isError, isUnitialized, isSuccessful }
}
